import { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

type ErrorMessageGetter = (issue: z.ZodIssueOptionalMessage, ctx: z.ErrorMapCtx) => string;

const Component = () => {
  const [t] = useTranslation(['backend', 'common', 'messages']);

  // eslint-disable-next-line no-restricted-syntax
  const getErrorMessage = useCallback<ErrorMessageGetter>(
    (issue, ctx) => {
      if (issue.code === z.ZodIssueCode.invalid_type) {
        if (issue.received === z.ZodParsedType.undefined || issue.received === z.ZodParsedType.null) {
          return t('messages:FIELD_IS_REQUIRED');
        }

        if (issue.expected === z.ZodParsedType.number) {
          return t('messages:SHOULD_BE_NUMBER');
        }
      }

      if (issue.code === z.ZodIssueCode.too_small) {
        if (issue.type === 'number') {
          return issue.inclusive
            ? t('messages:LIMIT_EXCEEDED_MIN', { min: issue.minimum })
            : t('messages:LIMITED_MIN', { min: issue.minimum });
        }

        if (issue.type === 'string') {
          if (issue.minimum === 1) {
            return t('messages:FIELD_IS_REQUIRED');
          }

          return t('messages:VALUE_LENGTH_EXCEEDED_MIN', { min: issue.minimum });
        }

        if (issue.type === 'array' && issue.minimum === 1) {
          return t('messages:AT_LEAST_ONE_ITEM_REQUIRED');
        }
      }

      if (issue.code === z.ZodIssueCode.too_big) {
        if (issue.type === 'number') {
          return issue.inclusive
            ? t('messages:LIMIT_EXCEEDED_MAX', { max: issue.maximum })
            : t('messages:LIMITED_MAX', { max: issue.maximum });
        }

        if (issue.type === 'string' && issue.inclusive) {
          return t('messages:VALUE_LENGTH_EXCEEDED_MAX', { max: issue.maximum });
        }
      }

      return ctx.defaultError;
    },
    [t],
  );

  // eslint-disable-next-line no-restricted-syntax
  const customErrorMap = useCallback<z.ZodErrorMap>(
    (issue, ctx) => ({
      message: getErrorMessage(issue, ctx),
    }),
    [getErrorMessage],
  );

  useEffect(() => {
    z.setErrorMap(customErrorMap);
  }, [customErrorMap]);

  return null;
};

const ErrorMessagesConfigurator = memo(Component);

export { ErrorMessagesConfigurator };
